import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, X, Lock, Info, Link2, Unlink, MapPin, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  useGoogleReviewsStatus,
  useGoogleReviews,
  useGoogleReview,
  useGoogleReviewSettings,
  useUpdateGoogleReviewSettings,
  useRequestGoogleReviewSync,
  useGoogleReviewLocations,
  useStartGoogleReviewsOAuth,
  useDisconnectGoogleReviews,
  useDiscoverGoogleReviewLocations,
  useSelectGoogleReviewLocation,
} from '../../api/hooks';
import { SectionHeader, Spinner, Tabs, EmptyState, DataTable, ConfirmModal } from '../../components/ui';
import { toast } from '../../store';
import { cn, fmtDateTime } from '../../lib/utils';
import {
  REVIEW_PAGE_SIZE,
  STAR_RATING_OPTIONS,
  SYNC_STATUS_OPTIONS,
  DRAFT_STATUS_OPTIONS,
  APPROVAL_STATUS_OPTIONS,
  PUBLISH_STATUS_OPTIONS,
  syncStatusLabel,
  draftStatusLabel,
  approvalStatusLabel,
  publishStatusLabel,
  connectionStatusLabel,
  providerStatusLabel,
  isGoogleConnected,
  isGoogleProviderConfigured,
  GOOGLE_NOT_CONNECTED_MESSAGE,
  buildReviewParams,
  settingsToForm,
  validateSettings,
  buildSettingsPayload,
  newSyncIdempotencyKey,
  getSyncFeedback,
  getActiveLocation,
  getSyncAvailability,
  SYNC_DISABLED_MESSAGES,
  extractAuthorizationUrl,
  getOAuthCallbackResult,
  stripOAuthParams,
  getDiscoverError,
  getConnectError,
  excerpt,
} from './googleReviews';

// ─── Small display pieces ──────────────────────────────────────────────────────
function Stars({ rating }) {
  const n = Number(rating) || 0;
  return (
    <span className="whitespace-nowrap" aria-label={`${n} out of 5 stars`}>
      <span className="text-amber">{'★'.repeat(n)}</span>
      <span className="text-txt-muted">{'★'.repeat(Math.max(0, 5 - n))}</span>
    </span>
  );
}

function MiniBadge({ label, tone = 'muted' }) {
  const tones = {
    muted: 'text-txt-muted border-bg-border',
    ok:    'text-bull bg-bull/10 border-bull/30',
    warn:  'text-warn bg-warn/10 border-warn/30',
    bad:   'text-bear bg-bear/10 border-bear/30',
    info:  'text-info bg-info/10 border-info/30',
  };
  return (
    <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded border text-2xs font-mono whitespace-nowrap', tones[tone] ?? tones.muted)}>
      {label}
    </span>
  );
}

function StatusCard({ label, value, sub, accent }) {
  return (
    <div className="card p-3 flex flex-col gap-1">
      <span className="text-2xs text-txt-label uppercase tracking-widest">{label}</span>
      <div className={cn('text-sm font-display font-semibold leading-tight', accent || 'text-txt-primary')}>
        {value ?? '—'}
      </div>
      {sub && <div className="text-2xs text-txt-muted font-mono">{sub}</div>}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</span>
      <div className="text-xs text-txt-primary break-words">{value ?? <span className="text-txt-muted">—</span>}</div>
    </div>
  );
}

// ─── Connection section (Stage 2B) ─────────────────────────────────────────────
// Exported for focused static-markup tests.
export function ConnectionSection() {
  const { data: status, isLoading, error, refetch } = useGoogleReviewsStatus();
  const startOAuth = useStartGoogleReviewsOAuth();
  const disconnectMutation = useDisconnectGoogleReviews();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [actionError, setActionError] = useState(null);

  const providerConfigured = isGoogleProviderConfigured(status);
  const connectionStatus = status?.connection_status;
  const connected = isGoogleConnected(connectionStatus);
  const needsReconnect = connectionStatus === 'REAUTH_REQUIRED' || connectionStatus === 'ERROR';

  const onConnect = () => {
    setActionError(null);
    startOAuth.mutate(undefined, {
      onSuccess: (res) => {
        const url = extractAuthorizationUrl(res);
        if (!url) {
          setActionError('Could not start Google sign-in. Try again.');
          return;
        }
        // Full browser navigation to Google — never an iframe, and the URL is
        // never logged or persisted because it carries OAuth state.
        window.location.assign(url);
      },
      onError: (err) => setActionError(getConnectError(err)),
    });
  };

  const onDisconnect = () => {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmDisconnect(false);
        toast.success('Google Business Profile disconnected');
      },
      onError: () => {
        setConfirmDisconnect(false);
        // Fixed frontend-owned wording — raw backend error text is never toasted.
        toast.error('Could not disconnect Google Business Profile. Try again.');
      },
    });
  };

  let body;
  if (isLoading) {
    body = <div className="flex items-center gap-2 text-xs text-txt-muted"><Spinner size={13} /> Checking connection…</div>;
  } else if (error) {
    body = (
      <div className="text-xs text-bear">
        {/* Fixed frontend-owned wording — raw backend error text is never rendered. */}
        Could not check the Google connection.
        <button onClick={() => refetch()} className="ml-2 underline text-txt-muted">Retry</button>
      </div>
    );
  } else if (!providerConfigured) {
    body = (
      <>
        <p className="text-xs text-txt-secondary flex-1">Google provider not configured.</p>
        <button disabled className="btn-primary text-xs flex items-center gap-1.5 py-1.5 disabled:opacity-60 disabled:cursor-not-allowed">
          <Link2 size={12} /> Connect Google Business Profile
        </button>
      </>
    );
  } else if (connected) {
    body = (
      <>
        <p className="text-xs text-bull flex items-center gap-1.5 flex-1">
          <CheckCircle2 size={13} /> Connected to Google Business Profile.
        </p>
        <button
          onClick={() => setConfirmDisconnect(true)}
          disabled={disconnectMutation.isPending}
          className="btn-ghost text-xs flex items-center gap-1.5 py-1.5 text-bear hover:bg-bear/10 disabled:opacity-60"
        >
          <Unlink size={12} /> {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </>
    );
  } else {
    body = (
      <>
        <p className="text-xs text-txt-secondary flex-1">
          {connectionStatus === 'REAUTH_REQUIRED' && 'Google authorization needs to be renewed. Reconnect to keep syncing reviews.'}
          {connectionStatus === 'ERROR' && 'There is a connection problem with Google. Reconnect to fix it.'}
          {connectionStatus === 'DISCONNECTED' && 'Google Business Profile is disconnected. Connect again to resume syncing.'}
          {connectionStatus === 'PENDING_CONFIGURATION' && 'Connection setup is incomplete. Connect again to finish.'}
          {connectionStatus === 'UNCONFIGURED' && 'Connect your Google Business Profile to import and manage reviews.'}
        </p>
        <button
          onClick={onConnect}
          disabled={startOAuth.isPending}
          className="btn-primary text-xs flex items-center gap-1.5 py-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {startOAuth.isPending && <Spinner size={12} className="text-current" />}
          <Link2 size={12} />
          {startOAuth.isPending ? 'Connecting…' : needsReconnect ? 'Reconnect Google' : 'Connect Google Business Profile'}
        </button>
      </>
    );
  }

  return (
    <div className="panel p-3 space-y-2" data-testid="gr-connection">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-2xs font-mono text-txt-label uppercase tracking-widest">Google Connection</span>
        {body}
      </div>
      {actionError && <p className="text-xs text-bear" role="alert">{actionError}</p>}
      <ConfirmModal
        open={confirmDisconnect}
        title="Disconnect Google Business Profile?"
        message="Syncing and location discovery stop until you reconnect. Imported reviews stay saved."
        confirmLabel="Disconnect"
        danger
        submitting={disconnectMutation.isPending}
        submittingLabel="Disconnecting…"
        onConfirm={onDisconnect}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </div>
  );
}

// ─── Locations section (Stage 2B) ──────────────────────────────────────────────
// Exported for focused static-markup tests.
export function LocationsSection({ connected, locationsQuery }) {
  const { data, isLoading, error, refetch } = locationsQuery;
  const discoverMutation = useDiscoverGoogleReviewLocations();
  const selectMutation = useSelectGoogleReviewLocation();
  const [actionError, setActionError] = useState(null);
  const items = data?.items ?? [];

  const onDiscover = () => {
    setActionError(null);
    discoverMutation.mutate(undefined, {
      onError: (err) => setActionError(getDiscoverError(err)),
    });
  };

  const onSelect = (id) => {
    setActionError(null);
    selectMutation.mutate(id, {
      onError: (err) => setActionError(
        err?.status === 404
          ? 'That location is no longer available. Run Discover Locations again.'
          : 'Could not select the location. Try again.',
      ),
    });
  };

  return (
    <div className="panel p-3 space-y-3" data-testid="gr-locations">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-2xs font-mono text-txt-label uppercase tracking-widest flex items-center gap-1.5">
          <MapPin size={12} /> Locations
        </span>
        <button
          onClick={onDiscover}
          disabled={!connected || discoverMutation.isPending}
          className="btn-ghost text-xs flex items-center gap-1.5 py-1 ml-auto border border-bg-border disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {discoverMutation.isPending && <Spinner size={12} className="text-current" />}
          {discoverMutation.isPending ? 'Discovering…' : 'Discover Locations'}
        </button>
      </div>

      {!connected && (
        <p className="text-xs text-txt-muted">Connect Google Business Profile first to discover and select a location.</p>
      )}

      {error && (
        <div className="text-xs text-bear">
          Could not load locations.
          <button onClick={() => refetch()} className="ml-2 underline text-txt-muted">Retry</button>
        </div>
      )}
      {actionError && <p className="text-xs text-bear" role="alert">{actionError}</p>}

      {isLoading ? (
        <div className="flex justify-center py-4"><Spinner /></div>
      ) : connected && items.length === 0 && !error ? (
        <p className="text-xs text-txt-muted">No Google Business Profile locations found. Run Discover Locations.</p>
      ) : items.length > 0 ? (
        <ul className="space-y-1.5">
          {items.map((loc) => (
            <li
              key={loc.id}
              className={cn(
                'flex items-center gap-3 flex-wrap rounded-lg border px-3 py-2',
                loc.is_active ? 'border-amber/40 bg-amber/5' : 'border-bg-border',
              )}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs text-txt-primary font-medium truncate">
                  {loc.display_name ?? loc.google_location_name}
                </p>
                {loc.display_name && (
                  <p className="text-2xs text-txt-muted font-mono truncate">{loc.google_location_name}</p>
                )}
                <p className="text-2xs text-txt-muted font-mono">
                  Last synced: {loc.last_synced_at ? fmtDateTime(loc.last_synced_at) : 'Never'}
                </p>
              </div>
              {loc.is_active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-2xs font-mono text-amber bg-amber/10 border-amber/30">
                  <CheckCircle2 size={11} /> Selected
                </span>
              ) : (
                <button
                  onClick={() => onSelect(loc.id)}
                  disabled={!connected || selectMutation.isPending}
                  className="btn-ghost text-2xs py-1 px-2 border border-bg-border disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {selectMutation.isPending ? 'Selecting…' : 'Select Location'}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ─── Review detail drawer (read-only) ──────────────────────────────────────────
function ReviewDetailDrawer({ reviewId, onClose }) {
  const { data: review, isLoading, error, refetch } = useGoogleReview(reviewId);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:max-w-[min(720px,70vw)] bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal md:self-start md:my-6 md:max-h-[calc(100vh-48px)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-bg-border shrink-0">
          <div>
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Review Detail</p>
            <h3 className="font-display font-semibold text-sm text-txt-primary leading-tight mt-0.5">
              {review?.reviewer_display_name ?? '…'}
            </h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg" aria-label="Close">
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
          {error && (
            <div className="text-xs text-bear py-3">
              {error.message}
              <button onClick={refetch} className="ml-2 underline text-txt-muted">Retry</button>
            </div>
          )}
          {review && (
            <div className="space-y-4">
              <div className="grid gap-2 sm:grid-cols-2">
                <DetailRow label="Reviewer" value={review.reviewer_display_name ?? 'Anonymous'} />
                <DetailRow label="Rating" value={<Stars rating={review.star_rating} />} />
                <DetailRow label="Review Date" value={review.google_created_at ? fmtDateTime(review.google_created_at) : null} />
                <DetailRow label="Location ID" value={String(review.location_id)} />
              </div>

              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Review</p>
                <p className="text-xs text-txt-primary whitespace-pre-wrap">{review.comment ?? '—'}</p>
              </div>

              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Current Google Reply</p>
                {review.current_google_reply_text ? (
                  <>
                    <p className="text-xs text-txt-primary whitespace-pre-wrap">{review.current_google_reply_text}</p>
                    <p className="text-2xs text-txt-muted font-mono mt-1">
                      Updated {fmtDateTime(review.current_google_reply_updated_at)}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-txt-muted">No reply published on Google yet.</p>
                )}
              </div>

              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Status</p>
                <div className="flex flex-wrap gap-1.5">
                  <MiniBadge label={`Sync: ${syncStatusLabel(review.sync_status)}`} />
                  <MiniBadge label={`Draft: ${draftStatusLabel(review.draft_status)}`} />
                  <MiniBadge label={`Approval: ${approvalStatusLabel(review.approval_status)}`} />
                  <MiniBadge label={`Publish: ${publishStatusLabel(review.publish_status)}`} />
                </div>
              </div>

              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Reply Drafts</p>
                {review.drafts?.length ? (
                  <div className="space-y-2">
                    {review.drafts.map((d) => (
                      <div key={d.id} className="panel p-2.5">
                        <div className="text-2xs text-txt-muted font-mono mb-1">
                          v{d.version_number} · {d.source} · {fmtDateTime(d.created_at)}
                        </div>
                        <p className="text-xs text-txt-primary whitespace-pre-wrap">{d.draft_text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-txt-muted">No reply drafts yet.</p>
                )}
              </div>

              <div>
                <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">History</p>
                {review.history?.length ? (
                  <div className="space-y-1">
                    {review.history.map((h) => (
                      <div key={h.id} className="flex items-center justify-between text-2xs font-mono text-txt-muted border-b border-bg-border/40 py-1">
                        <span className="text-txt-secondary">{h.event_type}</span>
                        <span>{fmtDateTime(h.created_at)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-txt-muted">No history yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reviews tab ───────────────────────────────────────────────────────────────
// Exported for focused static-markup tests.
export function ReviewsTab() {
  const [page, setPage] = useState(0);
  const [starRating, setStarRating] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [publishStatus, setPublishStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [syncFeedback, setSyncFeedback] = useState(null);

  const statusQuery = useGoogleReviewsStatus();
  const status = statusQuery.data;
  const locationsQuery = useGoogleReviewLocations();

  const params = useMemo(
    () => buildReviewParams({ page, starRating, syncStatus, draftStatus, approvalStatus, publishStatus, fromDate, toDate }),
    [page, starRating, syncStatus, draftStatus, approvalStatus, publishStatus, fromDate, toDate],
  );
  const { data, isLoading, error, refetch } = useGoogleReviews(params);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / REVIEW_PAGE_SIZE);

  const syncMutation = useRequestGoogleReviewSync();
  const connected = isGoogleConnected(status?.connection_status);
  const { activeLocation, activeCount } = getActiveLocation(locationsQuery.data?.items);

  const syncAvailability = getSyncAvailability({
    providerConfigured: isGoogleProviderConfigured(status),
    connectionStatus: status?.connection_status,
    activeCount,
    emergencyPause: !!status?.emergency_pause,
    syncPending: syncMutation.isPending,
  });

  const onSync = () => {
    if (!syncAvailability.enabled || !activeLocation) return;
    setSyncFeedback(null);
    syncMutation.mutate(
      { idempotency_key: newSyncIdempotencyKey(), sync_kind: 'MANUAL', location_id: activeLocation.id },
      {
        onSuccess: (attempt) => setSyncFeedback(getSyncFeedback(attempt)),
        onError: () => setSyncFeedback({ type: 'error', message: 'Review sync failed. Please try again later.' }),
      },
    );
  };

  const hasActiveFilters =
    starRating || syncStatus || draftStatus || approvalStatus || publishStatus || fromDate || toDate;

  const clearFilters = () => {
    setStarRating(''); setSyncStatus(''); setDraftStatus('');
    setApprovalStatus(''); setPublishStatus('');
    setFromDate(''); setToDate(''); setPage(0);
  };

  const columns = [
    {
      key: 'reviewer_display_name',
      label: 'Reviewer',
      render: (v) => <span className="text-txt-primary font-medium">{v ?? 'Anonymous'}</span>,
    },
    { key: 'star_rating', label: 'Rating', render: (v) => <Stars rating={v} /> },
    {
      key: 'comment',
      label: 'Review',
      render: (v) => <span className="text-txt-secondary">{excerpt(v) || '—'}</span>,
    },
    {
      key: 'google_created_at',
      label: 'Date',
      mobileHidden: true,
      render: (v) => <span className="whitespace-nowrap">{fmtDateTime(v)}</span>,
    },
    {
      key: 'current_google_reply_text',
      label: 'Google Reply',
      mobileHidden: true,
      render: (v) => (v ? <MiniBadge label="Replied" tone="ok" /> : <span className="text-txt-muted">—</span>),
    },
    {
      key: 'sync_status',
      label: 'Sync',
      hideUntilXl: true,
      render: (v) => <MiniBadge label={syncStatusLabel(v)} tone={v === 'SYNC_FAILED' ? 'bad' : 'muted'} />,
    },
    {
      key: 'draft_status',
      label: 'Draft',
      hideUntilXl: true,
      render: (v) => <MiniBadge label={draftStatusLabel(v)} tone={v === 'GENERATION_FAILED' ? 'bad' : 'muted'} />,
    },
    {
      key: 'approval_status',
      label: 'Approval',
      mobileHidden: true,
      render: (v) => (
        <MiniBadge
          label={approvalStatusLabel(v)}
          tone={v === 'APPROVED' ? 'ok' : v === 'BLOCKED_BY_POLICY' ? 'bad' : v === 'PENDING_APPROVAL' ? 'warn' : 'muted'}
        />
      ),
    },
    {
      key: 'publish_status',
      label: 'Publish',
      hideUntilXl: true,
      render: (v) => (
        <MiniBadge
          label={publishStatusLabel(v)}
          tone={v === 'PUBLISHED' ? 'ok' : v === 'PUBLISH_FAILED' ? 'bad' : 'muted'}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        <StatusCard
          label="Connection"
          value={statusQuery.isLoading ? '…' : connectionStatusLabel(status?.connection_status)}
          accent={
            connected
              ? 'text-bull'
              : status?.connection_status === 'REAUTH_REQUIRED' || status?.connection_status === 'ERROR'
                ? 'text-bear'
                : 'text-warn'
          }
        />
        <StatusCard
          label="Google Provider"
          value={statusQuery.isLoading ? '…' : providerStatusLabel(status?.google_provider_status)}
        />
        <StatusCard
          label="AI Provider"
          value={statusQuery.isLoading ? '…' : providerStatusLabel(status?.ai_provider_status)}
        />
        <StatusCard
          label="Emergency Pause"
          value={statusQuery.isLoading ? '…' : status?.emergency_pause ? 'Paused' : 'Off'}
          accent={status?.emergency_pause ? 'text-bear' : undefined}
        />
        <StatusCard
          label="Reviews"
          value={statusQuery.isLoading ? '…' : String(status?.reviews_total ?? 0)}
        />
        <StatusCard
          label="Last Sync"
          value={
            statusQuery.isLoading
              ? '…'
              : status?.last_sync_attempt
                ? fmtDateTime(status.last_sync_attempt.created_at)
                : 'Never'
          }
          sub={status?.last_sync_attempt?.status ? `Status: ${status.last_sync_attempt.status}` : undefined}
        />
      </div>

      {/* Unconfigured banner */}
      {!connected && (
        <div className="panel p-3 flex items-start gap-2.5 border-info/30 bg-info/5">
          <Info size={15} className="text-info shrink-0 mt-0.5" />
          <p className="text-xs text-txt-secondary">{GOOGLE_NOT_CONNECTED_MESSAGE}</p>
        </div>
      )}

      {/* Locations */}
      <LocationsSection connected={connected} locationsQuery={locationsQuery} />

      {/* Sync control */}
      <div className="panel p-3 flex flex-wrap items-center gap-3">
        <button
          onClick={onSync}
          disabled={!syncAvailability.enabled}
          className="btn-primary text-xs flex items-center gap-1.5 py-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {syncMutation.isPending && <Spinner size={12} className="text-current" />}
          {syncMutation.isPending ? 'Syncing…' : 'Sync Reviews'}
        </button>
        {!statusQuery.isLoading && !syncAvailability.enabled && syncAvailability.reasonKey && (
          <span className="text-2xs text-txt-muted">{SYNC_DISABLED_MESSAGES[syncAvailability.reasonKey]}</span>
        )}
        {!statusQuery.isLoading && syncAvailability.enabled && (
          <span className="text-2xs text-txt-muted">
            Imports the latest reviews for the selected location. Automatic publishing stays disabled.
          </span>
        )}
        {syncFeedback && (
          <p
            className={cn(
              'text-xs w-full',
              syncFeedback.type === 'error' ? 'text-bear' : syncFeedback.type === 'success' ? 'text-bull' : 'text-info',
            )}
            role="status"
          >
            {syncFeedback.message}
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="panel p-3 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <select value={starRating} onChange={(e) => { setStarRating(e.target.value); setPage(0); }} className="input-base text-xs w-32" aria-label="Filter by rating">
            <option value="">All ratings</option>
            {STAR_RATING_OPTIONS.map((s) => (
              <option key={s} value={String(s)}>{s} star{s > 1 ? 's' : ''}</option>
            ))}
          </select>
          <select value={syncStatus} onChange={(e) => { setSyncStatus(e.target.value); setPage(0); }} className="input-base text-xs w-36" aria-label="Filter by sync status">
            <option value="">Sync: all</option>
            {SYNC_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={draftStatus} onChange={(e) => { setDraftStatus(e.target.value); setPage(0); }} className="input-base text-xs w-36" aria-label="Filter by draft status">
            <option value="">Draft: all</option>
            {DRAFT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={approvalStatus} onChange={(e) => { setApprovalStatus(e.target.value); setPage(0); }} className="input-base text-xs w-40" aria-label="Filter by approval status">
            <option value="">Approval: all</option>
            {APPROVAL_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={publishStatus} onChange={(e) => { setPublishStatus(e.target.value); setPage(0); }} className="input-base text-xs w-36" aria-label="Filter by publish status">
            <option value="">Publish: all</option>
            {PUBLISH_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-2xs font-mono text-txt-muted">From:</span>
          <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }} className="input-base w-36 text-xs" />
          <span className="text-2xs font-mono text-txt-muted">To:</span>
          <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }} className="input-base w-36 text-xs" />
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-2xs text-txt-muted hover:text-txt-secondary flex items-center gap-1">
              <X size={11} /> Clear all
            </button>
          )}
          <button onClick={() => refetch()} className="btn-ghost text-xs flex items-center gap-1.5 py-1 ml-auto">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* List */}
      <div className="panel overflow-x-auto">
        {error ? (
          <div className="flex flex-col items-center py-12 gap-3 text-xs text-bear">
            <p>{error.message}</p>
            <button onClick={() => refetch()} className="btn-ghost text-xs">Retry</button>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No reviews yet"
            description="Reviews will appear here after Google Business Profile is connected and synchronized."
          />
        ) : (
          <DataTable columns={columns} data={items} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-txt-muted">
          <span>Page {page + 1} of {totalPages} ({total} total)</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedId != null && (
        <ReviewDetailDrawer reviewId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

// ─── Settings tab ──────────────────────────────────────────────────────────────
function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-2xs text-bear mt-1">{message}</p>;
}

// Exported for focused static-markup tests.
export function SettingsTab() {
  const { data: settings, isLoading, error, refetch } = useGoogleReviewSettings();
  const updateMutation = useUpdateGoogleReviewSettings();
  // Edits overlay the loaded settings so no effect is needed to hydrate the
  // form; a successful save clears the overlay and the refetched settings
  // become the new base.
  const [edits, setEdits] = useState({});

  const form = useMemo(
    () => (settings ? { ...settingsToForm(settings), ...edits } : null),
    [settings, edits],
  );
  const errors = useMemo(() => validateSettings(form), [form]);
  const payload = useMemo(() => buildSettingsPayload(form, settings), [form, settings]);
  const hasChanges = Object.keys(payload).length > 0;
  const canSave = !!form && hasChanges && Object.keys(errors).length === 0 && !updateMutation.isPending;

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEdits((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = () => {
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setEdits({});
        toast.success('Google Reviews settings saved');
      },
      onError: (err) => toast.error(err.message || 'Failed to save settings'),
    });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (error) {
    return (
      <div className="flex flex-col items-center py-12 gap-3 text-xs text-bear">
        <p>{error.message}</p>
        <button onClick={() => refetch()} className="btn-ghost text-xs">Retry</button>
      </div>
    );
  }
  if (!form) return null;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="panel p-4 space-y-4">
        <div>
          <p className="text-xs font-medium text-txt-primary">Automation</p>
          <p className="text-2xs text-txt-muted mt-0.5">
            These controls prepare automation behavior. They only take effect after Google Business Profile is connected.
          </p>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.automation_enabled}
            onChange={set('automation_enabled')}
            className="mt-0.5"
          />
          <span>
            <span className="text-xs text-txt-primary block">Enable automation</span>
            <span className="text-2xs text-txt-muted">Automatically check for new reviews on the polling interval.</span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.emergency_pause}
            onChange={set('emergency_pause')}
            className="mt-0.5"
          />
          <span>
            <span className="text-xs text-txt-primary block">Emergency pause</span>
            <span className="text-2xs text-txt-muted">Immediately pause all Google Reviews automation.</span>
          </span>
        </label>

        <div className="flex items-start gap-2.5 opacity-70">
          <input type="checkbox" checked={false} disabled className="mt-0.5" aria-label="Automatic publishing (disabled)" />
          <span>
            <span className="text-xs text-txt-primary flex items-center gap-1.5">
              Automatic publishing <Lock size={11} className="text-txt-muted" />
            </span>
            <span className="text-2xs text-txt-muted">
              Automatic publishing stays disabled in this stage. Replies will always need manual review before publishing.
            </span>
          </span>
        </div>

        <div>
          <label className="text-xs text-txt-primary block mb-1" htmlFor="gr-polling">Polling interval (minutes)</label>
          <input
            id="gr-polling"
            type="number"
            min={15}
            max={1440}
            step={1}
            value={form.polling_interval_minutes}
            onChange={set('polling_interval_minutes')}
            className="input-base text-xs w-40"
          />
          <p className="text-2xs text-txt-muted mt-1">How often new reviews are checked. Between 15 and 1440 minutes.</p>
          <FieldError message={errors.polling_interval_minutes} />
        </div>
      </div>

      <div className="panel p-4 space-y-4">
        <div>
          <p className="text-xs font-medium text-txt-primary">Review thresholds</p>
          <p className="text-2xs text-txt-muted mt-0.5">
            Decide which future reviews are treated as positive and which need manual approval.
          </p>
        </div>

        <div>
          <label className="text-xs text-txt-primary block mb-1" htmlFor="gr-positive">Positive review threshold (stars)</label>
          <select
            id="gr-positive"
            value={form.positive_review_min_stars}
            onChange={set('positive_review_min_stars')}
            className="input-base text-xs w-40"
          >
            {STAR_RATING_OPTIONS.map((s) => <option key={s} value={String(s)}>{s}</option>)}
          </select>
          <p className="text-2xs text-txt-muted mt-1">Reviews with this many stars or more count as positive.</p>
          <FieldError message={errors.positive_review_min_stars} />
        </div>

        <div>
          <label className="text-xs text-txt-primary block mb-1" htmlFor="gr-manual">Manual approval up to (stars)</label>
          <select
            id="gr-manual"
            value={form.manual_approval_max_stars}
            onChange={set('manual_approval_max_stars')}
            className="input-base text-xs w-40"
          >
            {STAR_RATING_OPTIONS.map((s) => <option key={s} value={String(s)}>{s}</option>)}
          </select>
          <p className="text-2xs text-txt-muted mt-1">
            Reviews with up to this many stars will need manual approval. Must be lower than the positive threshold.
          </p>
          <FieldError message={errors.manual_approval_max_stars} />
        </div>
      </div>

      <div className="panel p-4 space-y-4">
        <p className="text-xs font-medium text-txt-primary">Business identity</p>

        <div>
          <label className="text-xs text-txt-primary block mb-1" htmlFor="gr-bizname">Business display name</label>
          <input
            id="gr-bizname"
            type="text"
            maxLength={180}
            value={form.business_display_name}
            onChange={set('business_display_name')}
            className="input-base text-xs w-full max-w-md"
          />
          <FieldError message={errors.business_display_name} />
        </div>

        <div>
          <label className="text-xs text-txt-primary block mb-1" htmlFor="gr-email">Support email (optional)</label>
          <input
            id="gr-email"
            type="email"
            maxLength={254}
            value={form.support_email}
            onChange={set('support_email')}
            className="input-base text-xs w-full max-w-md"
            placeholder="support@example.com"
          />
          <FieldError message={errors.support_email} />
        </div>

        <DetailRow label="AI Provider" value={providerStatusLabel(settings?.ai_provider_status)} />
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={!canSave}
          className="btn-primary text-xs flex items-center gap-1.5 py-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {updateMutation.isPending && <Spinner size={12} className="text-current" />}
          {updateMutation.isPending ? 'Saving…' : 'Save Settings'}
        </button>
        {!hasChanges && <span className="text-2xs text-txt-muted">No changes to save.</span>}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
const PAGE_TABS = [
  { id: 'reviews', label: 'Reviews' },
  { id: 'settings', label: 'Settings' },
];

export default function AdminGoogleReviews() {
  const [tab, setTab] = useState('reviews');
  const [searchParams, setSearchParams] = useSearchParams();
  // Capture the OAuth callback result once on mount, then strip the params so
  // a refresh does not repeat the message. Only the two documented params are
  // read; unrelated query params are preserved.
  const [oauthResult, setOauthResult] = useState(() => getOAuthCallbackResult(searchParams.toString()));

  useEffect(() => {
    const cleaned = stripOAuthParams(searchParams.toString());
    if (cleaned !== null) {
      setSearchParams(cleaned, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="max-w-[90rem] space-y-5">
      <SectionHeader
        title="Google Reviews"
        subtitle="Monitor your Google Business Profile reviews and prepare reply automation."
      />

      {oauthResult && (
        <div
          className={cn(
            'panel p-3 flex items-start gap-2.5',
            oauthResult.type === 'success' ? 'border-bull/30 bg-bull/5' : 'border-bear/30 bg-bear/5',
          )}
          role="status"
        >
          {oauthResult.type === 'success'
            ? <CheckCircle2 size={15} className="text-bull shrink-0 mt-0.5" />
            : <AlertTriangle size={15} className="text-bear shrink-0 mt-0.5" />}
          <p className="text-xs text-txt-secondary flex-1">{oauthResult.message}</p>
          <button onClick={() => setOauthResult(null)} className="text-txt-muted hover:text-txt-secondary" aria-label="Dismiss">
            <X size={13} />
          </button>
        </div>
      )}

      <ConnectionSection />

      <Tabs tabs={PAGE_TABS} active={tab} onChange={setTab} />
      {tab === 'reviews' ? <ReviewsTab /> : <SettingsTab />}
    </div>
  );
}
